import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiJson } from "../lib/queryClient";
import LicenseCard, { FullLicenseCard } from "../components/LicenseCard";
import PhotoCropper from "../components/PhotoCropper";
import {
  BUNDESLAENDER,
  PARAGRAPH_21H_RULES,
  BUNDESLAND_AV_INFO,
  DIPUL_ANTRAG_URL,
  parseGermanDate,
  isErlaubnisExpired,
  migrateAllgemeinerlaubnis,
  type Bundesland,
  type GeoAllgemeinerlaubnis,
  type DocumentRef,
} from "../lib/allgemeinerlaubnis";

type Details = {
  firstName: string;
  lastName: string;
  identificationNumber: string;
  expirationDate: string;
  country?: string;
};

type PilotProfile = {
  id?: string;
  name: string;
  pilotId: string;
  operatorId: string;
  licenseA1A3: boolean;
  licenseA2: boolean;
  licenseSTS: boolean;
  licenseA1A3Details: Details | null;
  licenseA2Details: Details | null;
  photoUrl: string | null;
  insuranceActive: boolean;
  operatorRegistered: boolean;
  allgemeinverfuegung: GeoAllgemeinerlaubnis[];
};

const EMPTY: PilotProfile = {
  name: "",
  pilotId: "",
  operatorId: "",
  licenseA1A3: false,
  licenseA2: false,
  licenseSTS: false,
  licenseA1A3Details: null,
  licenseA2Details: null,
  photoUrl: null,
  insuranceActive: false,
  operatorRegistered: false,
  allgemeinverfuegung: [],
};

const EMPTY_DETAILS: Details = {
  firstName: "",
  lastName: "",
  identificationNumber: "",
  expirationDate: "",
  country: "DE",
};

export default function Profile() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<PilotProfile | null>({ queryKey: ["/api/profile"] });
  const [form, setForm] = useState<PilotProfile>(EMPTY);
  const [pendingPhoto, setPendingPhoto] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setForm({
        ...EMPTY,
        ...data,
        allgemeinverfuegung: migrateAllgemeinerlaubnis((data as any).allgemeinverfuegung),
      });
    }
  }, [data]);

  const save = useMutation({
    mutationFn: (p: PilotProfile) => apiJson("POST", "/api/profile", p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/profile"] }),
  });

  const photoMutation = useMutation<Pick<PilotProfile, "photoUrl">, Error, string | null>({
    mutationFn: (photoUrl) =>
      apiJson<Pick<PilotProfile, "photoUrl">>("POST", "/api/profile/photo", { photoUrl }),
    onSuccess: (saved) => {
      setForm((f) => ({ ...f, photoUrl: saved?.photoUrl ?? null }));
      qc.invalidateQueries({ queryKey: ["/api/profile"] });
    },
    onError: (e) => setPhotoError(e.message),
  });

  function onPhotoChange(e: ChangeEvent<HTMLInputElement>) {
    setPhotoError(null);
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setPhotoError("Bitte eine Bilddatei auswählen.");
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => setPhotoError("Datei konnte nicht gelesen werden");
    reader.onload = () => {
      if (typeof reader.result === "string") setPendingPhoto(reader.result);
    };
    reader.readAsDataURL(file);
  }

  function removePhoto() {
    setPhotoError(null);
    photoMutation.mutate(null);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate(form);
  }

  function setDetails(field: "licenseA1A3Details" | "licenseA2Details", patch: Partial<Details>) {
    setForm((f) => ({
      ...f,
      [field]: { ...EMPTY_DETAILS, ...(f[field] || {}), ...patch },
    }));
  }

  function addErlaubnis(bundesland: Bundesland) {
    setForm((f) => {
      if (f.allgemeinverfuegung.some((e) => e.bundesland === bundesland)) return f;
      return {
        ...f,
        allgemeinverfuegung: [
          ...f.allgemeinverfuegung,
          { bundesland, expirationDate: undefined, document: null },
        ],
      };
    });
  }

  function removeErlaubnis(bundesland: Bundesland) {
    setForm((f) => ({
      ...f,
      allgemeinverfuegung: f.allgemeinverfuegung.filter((e) => e.bundesland !== bundesland),
    }));
  }

  function updateErlaubnis(bundesland: Bundesland, patch: Partial<GeoAllgemeinerlaubnis>) {
    setForm((f) => ({
      ...f,
      allgemeinverfuegung: f.allgemeinverfuegung.map((e) =>
        e.bundesland === bundesland ? { ...e, ...patch } : e,
      ),
    }));
  }

  if (isLoading) return <div className="text-center text-[var(--color-app-text-secondary)] py-10">Lädt …</div>;

  return (
    <div className="space-y-6">
      {pendingPhoto && (
        <PhotoCropper
          src={pendingPhoto}
          onCancel={() => setPendingPhoto(null)}
          onConfirm={(dataUrl) => {
            setPendingPhoto(null);
            photoMutation.mutate(dataUrl);
          }}
        />
      )}
      <div className="card flex flex-wrap items-center gap-4">
        <div className="size-16 rounded-full overflow-hidden bg-[var(--color-app-bg-tertiary)] grid place-items-center border border-[var(--color-app-border)] shrink-0">
          {form.photoUrl ? (
            <img src={form.photoUrl} alt="Pilotenfoto" className="w-full h-full object-cover" />
          ) : (
            <span className="text-[10px] text-[var(--color-app-text-secondary)] text-center px-1">Kein Foto</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-lg font-semibold text-[var(--color-app-text)] truncate">
            {form.name || "Pilot & Lizenz"}
          </div>
          <div className="text-xs text-[var(--color-app-text-secondary)] truncate">
            {form.pilotId || "Keine Pilot-ID hinterlegt"}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Badge label="Versicherung" ok={form.insuranceActive} />
            <Badge label="Betreiber (e-ID)" ok={form.operatorRegistered} />
            {form.licenseA1A3 ? <Badge label="A1/A3" ok /> : null}
            {form.licenseA2 ? <Badge label="A2" ok /> : null}
            {form.licenseSTS ? <Badge label="STS" ok /> : null}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card">
          <LicenseCard
            pilotName={form.name}
            pilotId={form.pilotId}
            operatorId={form.operatorId}
            licenseA1A3={form.licenseA1A3}
            licenseA2={form.licenseA2}
            a1a3={form.licenseA1A3Details}
            a2={form.licenseA2Details}
            photoUrl={form.photoUrl}
          />
          <div className="mt-4 space-y-2">
            <h3 className="text-sm font-semibold text-[var(--color-app-text)]">Pilotenfoto</h3>
            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onPhotoChange}
                className="hidden"
              />
              <button
                type="button"
                className="btn-secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={photoMutation.isPending}
              >
                {photoMutation.isPending
                  ? "Lädt …"
                  : form.photoUrl
                    ? "Foto ändern"
                    : "Foto hochladen"}
              </button>
              {form.photoUrl ? (
                <button
                  type="button"
                  className="text-xs text-[var(--color-nogo)] hover:underline"
                  onClick={removePhoto}
                  disabled={photoMutation.isPending}
                >
                  Entfernen
                </button>
              ) : null}
            </div>
            <p className="text-[11px] text-[var(--color-app-text-secondary)]">
              JPG, PNG oder WebP. Nach der Auswahl kannst du den 2:3-Bildausschnitt wählen.
            </p>
            {photoError ? <div className="text-sm text-[var(--color-nogo)]">{photoError}</div> : null}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
            <Status label="Versicherung aktiv" ok={form.insuranceActive} />
            <Status label="Betreiber registriert (e-ID)" ok={form.operatorRegistered} />
          </div>
        </div>

        <form onSubmit={submit} className="card space-y-4">
          <h2 className="font-semibold text-[var(--color-app-text)]">Stammdaten</h2>
          <div>
            <label className="label">Pilotenname</label>
            <input className="input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Max Mustermann" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Pilot-ID</label>
              <input className="input" value={form.pilotId}
                onChange={(e) => setForm({ ...form, pilotId: e.target.value })} placeholder="DEU-RP-..." />
            </div>
            <div>
              <label className="label">Betreiber-ID (e-ID)</label>
              <input className="input" value={form.operatorId}
                onChange={(e) => setForm({ ...form, operatorId: e.target.value })} placeholder="DEUxxxxxxxxxxxxx" />
            </div>
          </div>

          <h2 className="font-semibold text-[var(--color-app-text)] pt-2">Lizenzen</h2>
          <div className="space-y-2">
            <CheckRow label="A1/A3 Online-Test" checked={form.licenseA1A3}
              onChange={(v) => setForm({ ...form, licenseA1A3: v })} />
            {form.licenseA1A3 && (
              <DetailFields details={form.licenseA1A3Details}
                onChange={(p) => setDetails("licenseA1A3Details", p)} />
            )}
            <CheckRow label="A2 Fernpilotenzeugnis" checked={form.licenseA2}
              onChange={(v) => setForm({ ...form, licenseA2: v })} />
            {form.licenseA2 && (
              <DetailFields details={form.licenseA2Details}
                onChange={(p) => setDetails("licenseA2Details", p)} />
            )}
            <CheckRow label="STS / Specific" checked={form.licenseSTS}
              onChange={(v) => setForm({ ...form, licenseSTS: v })} />
          </div>

          <h2 className="font-semibold text-[var(--color-app-text)] pt-2">Geografische Allgemeinerlaubnis (§ 21h LuftVO)</h2>
          <AllgemeinerlaubnisSection
            hasA2={form.licenseA2}
            erlaubnisse={form.allgemeinverfuegung}
            onAdd={addErlaubnis}
            onRemove={removeErlaubnis}
            onUpdate={updateErlaubnis}
          />

          <h2 className="font-semibold text-[var(--color-app-text)] pt-2">Status</h2>
          <CheckRow label="Haftpflichtversicherung aktiv" checked={form.insuranceActive}
            onChange={(v) => setForm({ ...form, insuranceActive: v })} />
          <CheckRow label="Betreiber beim LBA registriert" checked={form.operatorRegistered}
            onChange={(v) => setForm({ ...form, operatorRegistered: v })} />

          {save.error ? (
            <div className="text-sm text-[var(--color-nogo)]">{(save.error as Error).message}</div>
          ) : null}
          {save.isSuccess ? (
            <div className="text-sm text-[var(--color-go)]">Gespeichert.</div>
          ) : null}

          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={save.isPending} className="btn-primary flex-1">
              {save.isPending ? "Speichert …" : "Speichern"}
            </button>
            <Link to="/drohnen" className="btn-secondary">Zu Drohnen</Link>
          </div>
        </form>
      </div>

      {form.licenseA1A3 || form.licenseA2 ? (
        <div className="card space-y-4">
          <div>
            <h2 className="font-semibold text-[var(--color-app-text)]">Lizenzkarten</h2>
            <p className="text-xs text-[var(--color-app-text-secondary)] mt-0.5">
              Vollständige Karten zum Vorzeigen bei einer Kontrolle. Tippe auf
              "Vorzeigen" für die Vollbild-Ansicht.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {form.licenseA2 ? (
              <FullLicenseCard
                tier="A2"
                pilotName={form.name}
                pilotId={form.pilotId}
                details={form.licenseA2Details}
                photoUrl={form.photoUrl}
              />
            ) : null}
            {form.licenseA1A3 ? (
              <FullLicenseCard
                tier="A1A3"
                pilotName={form.name}
                pilotId={form.pilotId}
                details={form.licenseA1A3Details}
                photoUrl={form.photoUrl}
              />
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Badge({ label, ok }: { label: string; ok: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
        ok ? "bg-[var(--color-go-bg)] text-[var(--color-go)]" : "bg-[var(--color-app-bg-tertiary)] text-[var(--color-app-text-secondary)]"
      }`}
    >
      <span className={`size-1.5 rounded-full ${ok ? "bg-[var(--color-go)]" : "bg-[var(--color-app-text-tertiary)]"}`} />
      {label}
    </span>
  );
}

function CheckRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-3 text-sm cursor-pointer py-1">
      <span className="text-[var(--color-app-text)]">{label}</span>
      <span className="relative inline-flex shrink-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="peer sr-only"
        />
        <span className="block w-[44px] h-[26px] rounded-full bg-[var(--color-app-border)] transition-colors peer-checked:bg-brand-500" />
        <span className="absolute left-0.5 top-0.5 size-[22px] rounded-full bg-white shadow transition-transform peer-checked:translate-x-[18px]" />
      </span>
    </label>
  );
}

function Status({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className={`rounded-md px-2 py-1.5 ${ok ? "bg-[var(--color-go-bg)] text-[var(--color-go)]" : "bg-[var(--color-app-bg-secondary)] text-[var(--color-app-text-secondary)]"}`}>
      <div className="text-[10px] uppercase font-semibold">{label}</div>
      <div className="font-medium">{ok ? "Ja" : "Nein"}</div>
    </div>
  );
}

function DetailFields({ details, onChange }: { details: Details | null; onChange: (p: Partial<Details>) => void }) {
  const d = details || EMPTY_DETAILS;
  return (
    <div className="ml-6 grid grid-cols-2 gap-2 pb-2">
      <input className="input text-xs" placeholder="Vorname" value={d.firstName}
        onChange={(e) => onChange({ firstName: e.target.value })} />
      <input className="input text-xs" placeholder="Nachname" value={d.lastName}
        onChange={(e) => onChange({ lastName: e.target.value })} />
      <input className="input text-xs" placeholder="Nachweis-Nr." value={d.identificationNumber}
        onChange={(e) => onChange({ identificationNumber: e.target.value })} />
      <input className="input text-xs" placeholder="Gültig bis (TT.MM.JJJJ)" value={d.expirationDate}
        onChange={(e) => onChange({ expirationDate: e.target.value })} />
    </div>
  );
}

const MAX_DOCUMENT_BYTES = 15 * 1024 * 1024;

function AllgemeinerlaubnisSection({
  hasA2,
  erlaubnisse,
  onAdd,
  onRemove,
  onUpdate,
}: {
  hasA2: boolean;
  erlaubnisse: GeoAllgemeinerlaubnis[];
  onAdd: (b: Bundesland) => void;
  onRemove: (b: Bundesland) => void;
  onUpdate: (b: Bundesland, patch: Partial<GeoAllgemeinerlaubnis>) => void;
}) {
  const [showRules, setShowRules] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  if (!hasA2) {
    return (
      <div className="rounded-md border border-[var(--color-caution)] bg-[var(--color-caution-bg)] p-3 text-sm text-[var(--color-caution)] space-y-2">
        <p>
          Für die geografische Allgemeinerlaubnis nach § 21h LuftVO wird ein A2-Fernpilotenzeugnis
          benötigt. Aktiviere oben die A2-Lizenz, um Erlaubnisse je Bundesland zu hinterlegen.
        </p>
        <a
          href={DIPUL_ANTRAG_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--color-brand-600)] hover:underline"
        >
          Infos &amp; Antrag bei dipul.de ↗
        </a>
      </div>
    );
  }

  const available = BUNDESLAENDER.filter(
    (b) => !erlaubnisse.some((e) => e.bundesland === b),
  );

  return (
    <div className="space-y-3">
      <p className="text-[12px] text-[var(--color-app-text-secondary)]">
        Hinterlege je Bundesland deine geografische Allgemeinerlaubnis (Bescheid-PDF und
        Ablaufdatum). Es gibt keine gegenseitige Anerkennung zwischen Bundesländern.
      </p>

      {erlaubnisse.length === 0 ? (
        <div className="rounded-md border border-[var(--color-app-border)] bg-[var(--color-app-bg-secondary)] p-3 text-sm text-[var(--color-app-text-secondary)]">
          Noch keine Allgemeinerlaubnis hinterlegt.
        </div>
      ) : (
        <div className="space-y-2">
          {erlaubnisse.map((e) => (
            <ErlaubnisCard
              key={e.bundesland}
              erlaubnis={e}
              onRemove={() => onRemove(e.bundesland)}
              onUpdate={(patch) => onUpdate(e.bundesland, patch)}
            />
          ))}
        </div>
      )}

      {available.length > 0 ? (
        <div>
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={() => setShowAdd((s) => !s)}
          >
            {showAdd ? "Abbrechen" : "Bundesland hinzufügen"}
          </button>
          {showAdd ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {available.map((b) => (
                <button
                  key={b}
                  type="button"
                  className="rounded-full border border-[var(--color-app-border)] px-2.5 py-1 text-xs hover:bg-[var(--color-app-bg-tertiary)]"
                  onClick={() => {
                    onAdd(b);
                    setShowAdd(false);
                  }}
                >
                  {b}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="rounded-md border border-[var(--color-app-border)]">
        <button
          type="button"
          className="flex w-full items-center justify-between px-3 py-2 text-sm font-medium text-[var(--color-app-text)]"
          onClick={() => setShowRules((s) => !s)}
        >
          <span>Standardisierter Auflagen-Katalog (§ 21h)</span>
          <span className="text-[var(--color-app-text-tertiary)]">{showRules ? "−" : "+"}</span>
        </button>
        {showRules ? (
          <div className="space-y-2 border-t border-[var(--color-app-border)] px-3 py-2">
            {PARAGRAPH_21H_RULES.map((r) => (
              <div key={r.id}>
                <div className="text-xs font-semibold text-[var(--color-app-text)]">{r.label}</div>
                <div className="text-[11px] text-[var(--color-app-text-secondary)]">{r.auflagen}</div>
              </div>
            ))}
            <p className="text-[11px] text-[var(--color-app-text-tertiary)]">
              Natur-/Vogelschutz, Nationalparks und FFH-Gebiete sind nicht Teil der
              Allgemeinerlaubnis und erfordern eine separate Genehmigung.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function documentToBlobUrl(doc: DocumentRef): string {
  const binary = atob(doc.dataBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: doc.mimeType || "application/octet-stream" });
  return URL.createObjectURL(blob);
}

function openDocument(doc: DocumentRef) {
  const url = documentToBlobUrl(doc);
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function downloadDocument(doc: DocumentRef) {
  const url = documentToBlobUrl(doc);
  const a = document.createElement("a");
  a.href = url;
  a.download = doc.fileName || "allgemeinerlaubnis";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function ErlaubnisCard({
  erlaubnis,
  onRemove,
  onUpdate,
}: {
  erlaubnis: GeoAllgemeinerlaubnis;
  onRemove: () => void;
  onUpdate: (patch: Partial<GeoAllgemeinerlaubnis>) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const expired = isErlaubnisExpired(erlaubnis);
  const avInfo = BUNDESLAND_AV_INFO[erlaubnis.bundesland];
  const dateValid =
    !erlaubnis.expirationDate || parseGermanDate(erlaubnis.expirationDate) !== null;

  function onFileChange(ev: ChangeEvent<HTMLInputElement>) {
    setFileError(null);
    const file = ev.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (file.size > MAX_DOCUMENT_BYTES) {
      setFileError("Datei zu groß (max. 15 MB).");
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => setFileError("Datei konnte nicht gelesen werden.");
    reader.onload = () => {
      if (typeof reader.result !== "string") return;
      const base64 = reader.result.includes(",")
        ? reader.result.split(",")[1]
        : reader.result;
      const doc: DocumentRef = {
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
        uploadedAt: new Date().toISOString(),
        dataBase64: base64,
      };
      onUpdate({ document: doc });
    };
    reader.readAsDataURL(file);
  }

  return (
    <div className="rounded-md border border-[var(--color-app-border)] p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-[var(--color-app-text)]">{erlaubnis.bundesland}</span>
        <button
          type="button"
          className="text-xs text-[var(--color-nogo)] hover:underline"
          onClick={onRemove}
        >
          Entfernen
        </button>
      </div>

      <div>
        <label className="label">Gültig bis (TT.MM.JJJJ)</label>
        <input
          className="input text-sm"
          placeholder="TT.MM.JJJJ"
          value={erlaubnis.expirationDate || ""}
          onChange={(e) => onUpdate({ expirationDate: e.target.value || undefined })}
        />
        {!dateValid ? (
          <div className="mt-1 text-[11px] text-[var(--color-nogo)]">Ungültiges Datum (TT.MM.JJJJ).</div>
        ) : expired ? (
          <div className="mt-1 text-[11px] text-[var(--color-nogo)]">Abgelaufen.</div>
        ) : null}
      </div>

      <div>
        <label className="label">Bescheid-PDF</label>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,image/png,image/jpeg,image/webp"
            onChange={onFileChange}
            className="hidden"
          />
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={() => fileRef.current?.click()}
          >
            {erlaubnis.document ? "Datei ändern" : "Datei hochladen"}
          </button>
          {erlaubnis.document ? (
            <>
              <span className="text-xs text-[var(--color-app-text-secondary)] truncate max-w-[160px]">
                {erlaubnis.document.fileName}
              </span>
              <button
                type="button"
                className="text-xs text-[var(--color-brand-600)] hover:underline"
                onClick={() => openDocument(erlaubnis.document!)}
              >
                Öffnen
              </button>
              <button
                type="button"
                className="text-xs text-[var(--color-brand-600)] hover:underline"
                onClick={() => downloadDocument(erlaubnis.document!)}
              >
                Herunterladen
              </button>
              <button
                type="button"
                className="text-xs text-[var(--color-nogo)] hover:underline"
                onClick={() => onUpdate({ document: null })}
              >
                Entfernen
              </button>
            </>
          ) : null}
        </div>
        {fileError ? <div className="mt-1 text-[11px] text-[var(--color-nogo)]">{fileError}</div> : null}
      </div>

      <div className="rounded-md border border-[var(--color-app-border)] bg-[var(--color-app-bg-secondary)] p-2 text-[11px] text-[var(--color-app-text-secondary)]">
        Es gelten die bundesweit einheitlichen § 21h-Auflagen (siehe Katalog unten) für die
        erfassten Geozonen-Typen.
      </div>

      {avInfo ? (
        <div className="space-y-1">
          <div className="text-[11px] font-medium text-[var(--color-app-text-secondary)]">{avInfo.behorde}</div>
          <a
            href={avInfo.antragUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-xs font-semibold text-[var(--color-brand-600)] hover:underline"
          >
            Antrag stellen (dipul.de) ↗
          </a>
          {avInfo.infoUrl ? (
            <a
              href={avInfo.infoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-xs font-semibold text-[var(--color-brand-600)] hover:underline"
            >
              Infos der Landesluftfahrtbehörde ↗
            </a>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
